import { FC, useState, useEffect } from "react"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import { Card, Divider, Icon } from "@mui/material"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

import { getSessionUrl } from "../../../../api/session_url"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"

import ManageMedicalRecordUpdate from "../../../manage/medical-record/update"
import ManageMedicalRecordDelete from "../../../manage/medical-record/delete"
import DetectListOneDayModal from "../DetectListOneDayModal"
import SensitivityHistoryModal from "../../../components/SensitivityHistoryModal"
import { sensitivityLabel } from "../../../../constants/sensitivity"
import { ManageDeviceData } from "../../../manage/device/"

import { useConnectionError } from "../../../ConnectionErrorContext"

type PeriodValue = string[] | null

export interface MedicalRecordInfo {
  id?: string | null
  room_no?: string | null
  name?: string | null
  initial?: string | null
  age?: number | null
  birthday?: string | null
  address?: string | null
  devices?: ManageDeviceData[] | null
  devices_now?: ManageDeviceData[] | null
  device_places?: string[] | null
  setting_device_places?: string[] | null
  facility_id?: string | null
  facility_name?: string | null
  phone?: string | null
  detection?: number | null
  alert?: number | null
  memo?: string | null
  stop_mail?: number | null
  service_id?: string | null
  last_detect_date?: number | null
  last_detect?: string | null
  medical_record_display_type?: string | null
  is_poc?: number | null
  poc_start_date?: number | null

  sensitivity_enabled?: number | null
  sensitivity?: number | null
  detection_enabled?: number | null
  detection_period?: string[] | null
  option_enabled?: number | null
  option_period?: string[] | null

  effective_sensitivity_enabled?: number | null
  effective_sensitivity?: number | null
  effective_sensitivity_source?: string | null
  effective_detection_enabled?: number | null
  effective_detection_period?: string[] | null
  effective_detection_period_source?: string | null
  effective_option_enabled?: number | null
  effective_option_period?: string[] | null
  effective_option_period_source?: string | null

  reload?: () => void
}

const TenantInfo: FC<MedicalRecordInfo> = ({
  id,
  room_no,
  name,
  initial,
  age,
  birthday,
  address,
  is_poc,
  poc_start_date,
  devices,
  devices_now,
  device_places,
  setting_device_places,
  facility_id,
  facility_name,
  phone,
  detection,
  alert,
  memo,
  service_id,
  last_detect_date,
  last_detect,
  medical_record_display_type,
  sensitivity_enabled,
  sensitivity,
  detection_enabled,
  detection_period,
  option_enabled,
  option_period,
  effective_sensitivity,
  effective_sensitivity_source,
  effective_detection_period,
  effective_detection_period_source,
  effective_option_period,
  effective_option_period_source,
  reload
}) => {
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()
  const navigate = useNavigate()

  const formatSource = (source?: string | null) => {
    switch (source) {
      case "medical_record":
        return "入居者個別"
      case "catv":
        return "サービス提供元"
      case "system":
        return "システム設定"
      default:
        return "未設定"
    }
  }

  const formatPeriod = (period?: string[] | null) => {
    if (!period || period.length < 2 || !period[0] || !period[1]) {
      return "未設定"
    }

    return `${period[0]}〜${period[1]}`
  }

  const initialData: MedicalRecordInfo = {
    id,
    room_no,
    name,
    initial,
    age,
    birthday,
    address,
    is_poc,
    poc_start_date,
    devices,
    devices_now,
    device_places,
    setting_device_places,
    facility_id,
    facility_name,
    phone,
    detection,
    alert,
    last_detect_date,
    last_detect,
    medical_record_display_type,
    memo,
    service_id,
    sensitivity_enabled,
    sensitivity,
    detection_enabled,
    detection_period,
    option_enabled,
    option_period,
    effective_sensitivity,
    effective_sensitivity_source,
    effective_detection_period,
    effective_detection_period_source,
    effective_option_period,
    effective_option_period_source
  }

  const now = Math.floor(Date.now() / 1000)
  const POC_LIMIT_SECONDS = 30 * 24 * 60 * 60
  const isExpired =
    is_poc === 1 &&
    poc_start_date !== undefined &&
    poc_start_date !== null &&
    poc_start_date > 0 &&
    now - poc_start_date >= POC_LIMIT_SECONDS

  let status = "green"

  if (!id) {
    status = ""
  } else if (isExpired) {
    status = "dark"
  } else if (alert == -2) {
    status = "blue"
  } else if (alert == 0 || alert == -1) {
    status = "red"
  } else if (detection == 0) {
    status = "purple"
  }

  let deviceStatus = "オンライン"
  if (isExpired) {
    deviceStatus = "PoC期間終了"
  } else {
    switch (alert) {
      case 0:
        deviceStatus = "オフライン"
        break
      case -1:
        deviceStatus = "機器エラー"
        break
      case -2:
        deviceStatus = "デバイス未登録"
        break
    }
  }
  if (!id) {
    deviceStatus = ""
  }

  const getBackgroundColor = (status: string) => {
    switch (status) {
      case "dark":
        return "linear-gradient(195deg, #42424a, #191919)"
      case "blue":
        return "linear-gradient(195deg, #49a3f1, #1A73E8)"
      case "green":
        return "linear-gradient(195deg, #66BB6A, #43A047)"
      case "red":
        return "linear-gradient(195deg, #ef5350, #e53935)"
      case "purple":
        return "linear-gradient(195deg, #FFA726, #FB8C00)"
      default:
        return "#f0f0f0"
    }
  }

  const getTextColor = (status: string) => {
    return status === "blue" || status === "green" || status === "red" || status === "purple" || status === "dark"
      ? "#ffffff"
      : "#344767"
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "dark":
        return "history_toggle_off"
      case "blue":
        return "pending"
      case "green":
        return "check_circle"
      case "red":
        return "error"
      case "purple":
        return "warning"
    }
    return "check_circle"
  }

  const [medicalRecordUpdate, setMedicalRecordUpdate] = useState<boolean>(false)
  const [medicalRecordDelete, setMedicalRecordDelete] = useState<boolean>(false)
  const [showUpdateMedicalRecord, setShowUpdateMedicalRecord] = useState<boolean>(false)
  const [showSetupDevice, setShowSetupDevice] = useState<boolean>(false)
  const [showDeleteMedicalRecord, setShowDeleteMedicalRecord] = useState<boolean>(false)
  const [sensitivityOnlyEdit, setSensitivityOnlyEdit] = useState<boolean>(false)
  const [showSessionLink, setShowSessionLink] = useState<boolean>(false)
  const [showSensitivityHistory, setShowSensitivityHistory] = useState<boolean>(false)
  const [sessionUrl, setSessionUrl] = useState<string>("")
  const [qrCode, setQrCode] = useState<string>("")
  const [showDetectListOneDayModal, setShowDetectListOneDayModal] = useState<boolean>(false)
  const [confirmCreateZone, setConfirmCreateZone] = useState<boolean>(false)
  const [isZone, setIsZone] = useState<boolean>(false)

  useEffect(() => {
    const accountType = localStorage.getItem("account_type")
    const authFacility = localStorage.getItem("auth_facility")
    const viewMode = localStorage.getItem("view_mode")
    const showModeEstate = localStorage.getItem("show_mode_estate")

    const hasFacilityEditPermission = authFacility == "1"
    const isDeveloperAccount = accountType == "service"
    const isCatvAccount = accountType == "catv"
    const isEstateAccount = !isDeveloperAccount && !isCatvAccount

    const isImpersonationEditMode = isDeveloperAccount && viewMode == "2"
    const isImpersonatingEstate =
      isImpersonationEditMode && showModeEstate !== null && showModeEstate !== "" && showModeEstate !== "-"
    const isImpersonatingCatv = isImpersonationEditMode && !isImpersonatingEstate

    // 編集ボタン:
    // - サービス提供元アカウントは、感度・検知時間帯のみ編集可能
    // - 不動産アカウントは、建物編集権限がある場合だけ編集可能
    // - 開発元アカウントは、閲覧編集モードでCATV事業者または不動産業者に成りすましている場合だけ編集可能
    const canEditMedicalRecord =
      isCatvAccount || (isEstateAccount && hasFacilityEditPermission) || isImpersonatingCatv || isImpersonatingEstate

    // 削除ボタン:
    // - 建物の編集権限がある不動産アカウント
    // - 開発元アカウントが不動産業者に閲覧編集モードで成りすましている場合
    const canDeleteMedicalRecord = (isEstateAccount && hasFacilityEditPermission) || isImpersonatingEstate

    // デバイス登録 / 検知ログ確認:
    // - サービス提供元アカウント
    // - 不動産アカウント
    // - 開発元アカウントが閲覧編集モードでCATV事業者または不動産業者に成りすましている場合
    const canSetupDevice = isCatvAccount || isEstateAccount || isImpersonatingCatv || isImpersonatingEstate

    // 感度・検知時間帯のみ編集:
    // - サービス提供元アカウント
    // - 開発元アカウントがCATV事業者に成りすましている場合
    //
    // 不動産アカウントで建物編集権限がない場合は、
    // canEditMedicalRecord 自体が false なので編集不可。
    const shouldSensitivityOnlyEdit = isCatvAccount || isImpersonatingCatv

    setShowUpdateMedicalRecord(canEditMedicalRecord)
    setShowDeleteMedicalRecord(canDeleteMedicalRecord)
    setShowSetupDevice(canSetupDevice)
    setSensitivityOnlyEdit(shouldSensitivityOnlyEdit)
  }, [])

  const handleShowDetectListOneDayModal = () => {
    setShowDetectListOneDayModal(true)
  }
  const handleEdit = () => {
    setMedicalRecordUpdate(true)
  }
  const handleDelete = () => {
    setMedicalRecordDelete(true)
  }
  const handleClose = (num: number) => {
    if (num == 1 && reload) reload()
    setMedicalRecordUpdate(false)
  }
  const handleDeleteClose = (num: number) => {
    if (num == 1) {
      setMedicalRecordDelete(false)
      navigate(`/facility/${facility_id}`)
    } else {
      setMedicalRecordDelete(false)
    }
  }
  const handleShowDetectListOneDayModalClose = () => {
    setShowDetectListOneDayModal(false)
  }
  const handleSessionUrl = async () => {
    try {
      startLoading()
      const response = await getSessionUrl(id ? id.split("|")[1] : "")
      setSessionUrl(response.url)
      setQrCode(response.qr_code)
      setShowSessionLink(true)
      setIsZone(true)
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }
  const handleSessionUrlCheck = async () => {
    if (service_id === "" && !isZone) {
      setConfirmCreateZone(true)
      return
    }
    setIsZone(true)
    handleSessionUrl()
  }

  const formatInitials = (initialValue: string): string => {
    if (!initialValue) return ""
    return (
      initialValue
        .split("")
        .filter((char) => char.trim() !== "")
        .join(".") + "."
    )
  }

  return (
    <>
      {showDetectListOneDayModal && initialData && (
        <DetectListOneDayModal medical_record_id={id} onClose={handleShowDetectListOneDayModalClose} />
      )}
      {medicalRecordUpdate && initialData && (
        <ManageMedicalRecordUpdate
          facilityId={facility_id ? facility_id : ""}
          initialData={initialData}
          deviceData={devices ? devices : []}
          deviceDataNow={devices_now ? devices_now : []}
          devicePlace={setting_device_places ? setting_device_places : []}
          sensitivityOnly={sensitivityOnlyEdit}
          onClose={handleClose}
        />
      )}
      {medicalRecordDelete && initialData && (
        <ManageMedicalRecordDelete
          facilityId={facility_id ? facility_id : ""}
          initialData={initialData}
          deviceData={devices ? devices : []}
          deviceDataNow={devices_now ? devices_now : []}
          devicePlace={setting_device_places ? setting_device_places : []}
          onClose={handleDeleteClose}
        />
      )}
      {showSensitivityHistory && id && (
        <SensitivityHistoryModal
          medicalRecordId={id}
          title={`${facility_name || ""} ${room_no || ""} ${name || initial || ""} 感度変更履歴`}
          onClose={() => setShowSensitivityHistory(false)}
        />
      )}
      {confirmCreateZone && (
        <div className="modal-bg-cover">
          <Card
            sx={{
              width: "90%",
              maxWidth: "500px",
              maxHeight: "calc(100vh - 100px)",
              marginTop: "50px",
              padding: "20px"
            }}
          >
            <button className="close-button" onClick={() => setConfirmCreateZone(false)}>
              ×
            </button>
            <MDTypography variant="h4" fontWeight="medium">
              デバイス登録確認
            </MDTypography>
            <MDBox style={{ height: "20px" }}></MDBox>
            <MDBox style={{ fontSize: "16px" }}>
              この操作を行うと、<strong>ご利用料金の算定対象</strong>となります。
              <br />
              続けてもよろしいですか？
            </MDBox>
            <MDBox mt={4} mb={2} mr={2}>
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                onClick={async () => {
                  await handleSessionUrl()
                  setConfirmCreateZone(false)
                }}
              >
                続ける
              </MDButton>
            </MDBox>
          </Card>
        </div>
      )}
      {showSessionLink && (
        <div className="modal-bg-cover">
          <Card
            sx={{
              width: "90%",
              maxWidth: "500px",
              maxHeight: "calc(100vh - 100px)",
              marginTop: "50px",
              padding: "20px"
            }}
          >
            <button className="close-button" onClick={() => setShowSessionLink(false)}>
              ×
            </button>
            <MDTypography variant="h4" fontWeight="medium">
              デバイス登録アプリ起動
            </MDTypography>
            <MDBox style={{ height: "20px" }}></MDBox>
            <MDBox mb={3} textAlign="center">
              <MDTypography variant="body2" color="text">
                スマートフォンで <strong>Nami Setupアプリ</strong> を起動し、
                <br />
                以下のQRコードを読み取ってください。
                <br />
                詳しい手順は
                <a
                  href="https://help.sync-smile.com/data/catv/device_setup"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  こちら
                </a>
              </MDTypography>
            </MDBox>
            <MDBox display="flex" justifyContent="center" alignItems="center" width="100%" my={2}>
              {qrCode ? (
                <MDBox
                  component="img"
                  src={qrCode}
                  alt="QR Code"
                  sx={{
                    width: "250px",
                    height: "250px",
                    border: "1px solid #eee",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    display: "block",
                    margin: "0 auto"
                  }}
                />
              ) : (
                <MDBox sx={{ height: "250px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <MDTypography variant="caption">QRコードを生成中...</MDTypography>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </div>
      )}
      <Grid container spacing={3}>
        <Grid item sm={4} xs={12}>
          <Card
            sx={{
              background: getBackgroundColor(status),
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: 3
            }}
          >
            <Icon sx={{ color: getTextColor(status), fontSize: "60px !important", marginBottom: "20px" }}>
              {getStatusIcon(status)}
            </Icon>
            {!isExpired && (
              <>
                <MDTypography component="p" variant="button" sx={{ color: getTextColor(status) }}>
                  デバイス状態: {deviceStatus}
                </MDTypography>
                <MDTypography component="p" variant="button" sx={{ color: getTextColor(status) }}>
                  デバイス設置場所: {device_places?.join(", ")}
                </MDTypography>
              </>
            )}
            {isExpired && (
              <MDTypography component="p" variant="button" sx={{ color: getTextColor(status) }}>
                {deviceStatus}
              </MDTypography>
            )}
          </Card>
        </Grid>
        <Grid item sm={8} xs={12}>
          <Card sx={{ height: "100%" }}>
            <MDBox display="flex" justifyContent="space-between" px={3} pt={3}>
              <MDBox
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="flex-start"
                sx={{ width: "100%" }}
              >
                <MDTypography display="flex" variant="h2" fontWeight="bold" sx={{ width: "100%" }}>
                  <MDBox sx={{ width: "100%" }}>
                    {age !== undefined && age != 0 ? name : formatInitials(name ? name : "")}
                  </MDBox>
                  {showUpdateMedicalRecord && (
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="wrap"
                      justifyContent="flex-end"
                      sx={{ fontSize: "0.875rem", color: "text.primary", textAlign: "right", width: "100%" }}
                    >
                      {showSetupDevice && !isExpired && (
                        <>
                          <MDBox>
                            <MDButton
                              variant="gradient"
                              color="info"
                              size="medium"
                              onClick={handleShowDetectListOneDayModal}
                            >
                              検知ログ確認
                            </MDButton>
                          </MDBox>
                          <MDBox>
                            <MDButton variant="gradient" color="info" size="medium" onClick={handleSessionUrlCheck}>
                              デバイス登録
                            </MDButton>
                          </MDBox>
                        </>
                      )}
                      <MDBox>
                        <MDButton variant="gradient" color="info" size="medium" onClick={handleEdit}>
                          編集
                        </MDButton>
                      </MDBox>
                      {showDeleteMedicalRecord && (
                        <MDBox>
                          <MDButton variant="gradient" color="info" size="medium" onClick={handleDelete}>
                            削除
                          </MDButton>
                        </MDBox>
                      )}
                    </MDBox>
                  )}
                </MDTypography>
                <MDTypography variant="h4">
                  {initial ? formatInitials(initial) : ""} {age !== undefined && age != 0 ? age + "歳" : ""}
                </MDTypography>
              </MDBox>
            </MDBox>
            <Divider />
            <MDBox pb={3} px={3}>
              <MDTypography variant="h5">
                {facility_name} {room_no}
              </MDTypography>
              <MDTypography variant="body2">{address}</MDTypography>
              <MDTypography variant="body2">{phone}</MDTypography>

              <MDBox mt={2} p={2} sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                <MDTypography variant="h6">現在の感度設定</MDTypography>
                <MDTypography variant="body2">
                  感度: {sensitivityLabel(effective_sensitivity)}（{formatSource(effective_sensitivity_source)}）
                </MDTypography>

                <MDTypography variant="body2">
                  検知時間帯: {formatPeriod(effective_detection_period)}（
                  {formatSource(effective_detection_period_source)}）
                </MDTypography>

                <MDTypography variant="body2">
                  検知時間帯（オプション）: {formatPeriod(effective_option_period)}（
                  {formatSource(effective_option_period_source)}）
                </MDTypography>
                <MDBox mt={1}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => setShowSensitivityHistory(true)}
                  >
                    感度変更履歴
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

export default TenantInfo
